import { DashboardType } from 'lib-dashboard-types';
import { ampli } from 'lib-shared-tracking';
import { useState } from 'react';
import { useEffectOnce, useInterval } from 'react-use';

import { appActions } from 'app/app.actions';
import { pubSub } from 'utils/pubSub';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

/**
 * A hook that listens to a pubSub event and reloads the TV dashboard page. This
 * typically happens nightly at midnight in the organization's preferred
 * timezone (UTC by default), where certain metrics are reset.
 *
 * When this happens, a modal is shown informing the user that the dashboard is
 * about to be reloaded in a few seconds, with the option to cancel the reload.
 */
export const useTvDashboardReloadEffect = (dashboard: DashboardType): void => {
  const dispatch = useAppDispatch();

  useEffectOnce(() => {
    const unsubscribe = pubSub.subscribe(
      'real_time_dashboard:reset',
      handleReload,
    );

    return () => unsubscribe();
  });

  const handleReload = async () => {
    const { isConfirmed } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: 'A new version is available',
            description: <Countdown dashboard={dashboard} />,
            confirm: {
              label: 'Reload',
              variant: 'primary',
            },
            cancel: {
              label: 'Cancel',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      window.location.reload();
    }
  };
};

const Countdown = ({
  dashboard,
}: {
  dashboard: DashboardType;
}): JSX.Element => {
  const [count, setCount] = useState(10);

  useInterval(() => {
    if (count === 0) {
      ampli.dashboardTvReloaded({ dashboard });
      window.location.reload();
    } else {
      setCount((prevCount) => prevCount - 1);
    }
  }, 1000);

  return (
    <>
      This dashboard will reload in {count} seconds to apply the latest
      updates...
    </>
  );
};
